import { useMemo } from 'react'

import type { SelectOption } from '../../copSubscription/components/SearchableSelect'
import { USER_MANAGEMENT_DEFAULTS as D } from '../UserManagement.constants'
import type { UserFormData } from './useUserManagementForm'

// Role labels disabled for NoSLA customer selection AND always for logged-in User Manager
// Normalized (no spaces) so they match camelCase labels like "CustomerManager", "InternalSystemIntegrator"
const RESTRICTED_ROLE_TOKENS = ['customermanager', 'systemintegrator']

function isRestrictedRole(label: string): boolean {
    const normalized = label.toLowerCase().replace(/[\s_-]/g, '')
    return RESTRICTED_ROLE_TOKENS.some(token => normalized.includes(token))
}

function isNoSlaOption(label: string, value: string): boolean {
    // match on customer ID only — label may contain "No SLA" text for non-NOSLA accounts
    return /^nosla$/i.test(value)
}

function readLoggedInUserIsManager(): boolean {
    try {
        const raw = sessionStorage.getItem(D.SESSION_KEY_CURRENT_USER)
        if (!raw) return false
        const user = JSON.parse(raw) as { isUserManager?: boolean }
        return user?.isUserManager === true
    } catch {
        return false
    }
}

export type UserManagementRules = {
    isAdminOrManager: boolean
    isNoSlaCustomer: boolean
    hideProductDropdown: boolean
    disabledRoleValues: Set<string>
    // Tab 3 is always visible but locked for admin/manager
    tab3Disabled: boolean
    // True when the currently logged-in user holds the COP User Manager role
    isLoggedInAsUserManager: boolean
    noSlaCustomerValue: string | undefined
}

export function useUserManagementRules(
    formData: UserFormData,
    customerOptions: SelectOption[],
    roleOptions: SelectOption[],
): UserManagementRules {
    const isAdminOrManager =
        formData.userType === D.USER_TYPE_ADMIN || formData.userType === D.USER_TYPE_MANAGER

    const isLoggedInAsUserManager = useMemo(() => readLoggedInUserIsManager(), [])

    const isNoSlaCustomer = useMemo(() => {
        if (!formData.selectedCustomer) return false
        const opt = customerOptions.find(o => o.value === formData.selectedCustomer)
        return isNoSlaOption(opt?.label ?? '', formData.selectedCustomer)
    }, [formData.selectedCustomer, customerOptions])

    // First NO SLA option available — auto-selected for logged-in User Managers
    const noSlaCustomerValue = useMemo(() => {
        if (!isLoggedInAsUserManager) return undefined
        return customerOptions.find(o => isNoSlaOption(o.label, o.value))?.value
    }, [isLoggedInAsUserManager, customerOptions])

    const disabledRoleValues = useMemo(() => {
        if (!isNoSlaCustomer && !isLoggedInAsUserManager) return new Set<string>()
        return new Set(
            roleOptions
                .filter(o => isRestrictedRole(o.label))
                .map(o => o.value),
        )
    }, [isNoSlaCustomer, isLoggedInAsUserManager, roleOptions])

    return {
        isAdminOrManager,
        isNoSlaCustomer,
        hideProductDropdown: isNoSlaCustomer || isLoggedInAsUserManager || formData.userType === D.USER_TYPE_MANAGER,
        disabledRoleValues,
        // Tab 3 is only locked for system admins; managers need it to assign the NOSLA account
        tab3Disabled: formData.userType === D.USER_TYPE_ADMIN,
        isLoggedInAsUserManager,
        noSlaCustomerValue,
    }
}
